import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Language as GlobeIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

function getHostname(url) {
  try { return new URL(url).hostname; } catch { return url; }
}

// a page's image at least this wide is a photo worth showing across the card; smaller ones (logos, icons)
// stay a thumbnail beside the title
const BANNER_MIN_WIDTH = 400;
// the site's own image (its logo or default share image, the same on every page) adds nothing shown large: the
// server marks it image_kind 'site'; for older cards without that mark, its file name usually says so
const SITE_IMAGE_NAME = /(logo|favicon|icon|default|placeholder|og[-_]?image|share[-_]?image|social|brand)/i;
const isSiteImage = (preview) => preview.image_kind === 'site'
  || (preview.image_kind !== 'page' && SITE_IMAGE_NAME.test((preview.image || '').split('?')[0].split('/').pop()));

export default function LinkPreviewCard({ preview, onDismiss }) {
  const theme = useTheme();
  const [image, setImage] = useState('pending');      // 'pending' | 'banner' | 'thumb' | 'none'
  if (!preview?.url) return null;

  // where the link really goes, from its address (not the name the page gives itself): the card is the link
  const siteName = getHostname(preview.url).replace(/^www\./, '');
  const banner = image === 'banner';
  const img = preview.image && image !== 'none' && (
    <Box
      component="img"
      src={preview.image}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onLoad={(e) => setImage(!isSiteImage(preview) && e.currentTarget.naturalWidth >= BANNER_MIN_WIDTH ? 'banner' : 'thumb')}
      onError={() => setImage('none')}
      sx={banner
        ? { display: 'block', width: '100%', maxHeight: 300, objectFit: 'cover', bgcolor: 'action.hover' }
        : { width: 80, height: 80, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
    />
  );

  return (
    <Box
      component="a"
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: 'flex',
        flexDirection: banner ? 'column' : 'row',
        gap: banner ? 0 : 1.5,
        p: banner ? 0 : 2.5,
        mt: 2,
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.15s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {img}

      <Box sx={{ flex: 1, minWidth: 0, ...(banner && { px: 2, py: 1.5 }) }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.3 }}
        >
          <GlobeIcon sx={{ fontSize: 11 }} />
          {siteName}
        </Typography>
        {preview.title && (
          <Typography variant="body2" fontWeight={600} noWrap>
            {preview.title}
          </Typography>
        )}
        {preview.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {preview.description}
          </Typography>
        )}
      </Box>

      {onDismiss && (
        <IconButton
          size="small"
          aria-label="Remove the link preview"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(); }}
          sx={{ position: 'absolute', top: 4, right: 4, opacity: 0.8, '&:hover': { opacity: 1 },
            ...(banner && { color: '#fff', bgcolor: 'rgba(0,0,0,0.55)', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }) }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
}
